import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  CampaignChannel, CampaignType, CampaignSegment, FilterGroup, WaTemplate,
} from '../../types';
import {
  fetchSegments, fetchWaTemplates, createCampaign, updateCampaign, fetchCampaign,
  createSegment,
} from '../../services/api';
import FilterBuilder from './FilterBuilder';

// ── State ───────────────────────────────────────────────────────────────────

interface BuilderState {
  step: 1 | 2 | 3 | 4 | 5;
  name: string;
  description: string;
  channel: CampaignChannel;
  type: CampaignType;
  segmentId: number | null;
  segmentFilters: FilterGroup;
  segmentEntityType: string;
  segmentReach: number;
  templateId: number | null;
  messageBody: string;
  sourceId: number | null;
  dripDelayDays: number;
  scheduledAt: string;
  sendNow: boolean;
  throttlePerMinute: number;
}

const empty: BuilderState = {
  step: 1, name: '', description: '', channel: 'whatsapp', type: 'broadcast',
  segmentId: null, segmentFilters: { operator: 'AND', rules: [] }, segmentEntityType: 'lead',
  segmentReach: 0, templateId: null, messageBody: '', sourceId: null, dripDelayDays: 0,
  scheduledAt: '', sendNow: true, throttlePerMinute: 60,
};

const canProceed = (s: BuilderState): boolean => {
  if (s.step === 1) return !!s.name && !!s.channel && !!s.type;
  if (s.step === 2) return !!s.segmentId || s.segmentFilters.rules.length > 0;
  if (s.step === 3) return !!s.templateId || !!s.messageBody;
  if (s.step === 4) return s.sendNow || !!s.scheduledAt;
  return true;
};

// ── Sub-steps ───────────────────────────────────────────────────────────────

const Step1: React.FC<{ s: BuilderState; set: (k: keyof BuilderState, v: any) => void }> = ({ s, set }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
    <div>
      <label className="form-label">Campaign Name *</label>
      <input className="form-input" placeholder="e.g. May Punjab Broadcast" value={s.name} onChange={e => set('name', e.target.value)} />
    </div>
    <div>
      <label className="form-label">Description</label>
      <textarea className="form-input" rows={2} value={s.description} onChange={e => set('description', e.target.value)} style={{ resize: 'none' }} />
    </div>
    <div>
      <label className="form-label">Channel *</label>
      <div style={{ display: 'flex', gap: 10 }}>
        {(['whatsapp', 'sms', 'email'] as CampaignChannel[]).map(ch => (
          <button key={ch} onClick={() => set('channel', ch)} style={{
            flex: 1, padding: '12px 0', borderRadius: 10, border: '2px solid',
            borderColor: s.channel === ch ? '#7c3aed' : 'var(--border)',
            background: s.channel === ch ? '#ede9fe' : '#fff',
            color: s.channel === ch ? '#6d28d9' : 'var(--text)', fontWeight: 600, cursor: 'pointer',
          }}>
            {{ whatsapp: '💬 WhatsApp', sms: '📱 SMS', email: '✉️ Email' }[ch]}
          </button>
        ))}
      </div>
    </div>
    <div>
      <label className="form-label">Type *</label>
      <div style={{ display: 'flex', gap: 10 }}>
        {(['broadcast', 'drip'] as CampaignType[]).map(t => (
          <button key={t} onClick={() => set('type', t)} style={{
            flex: 1, padding: '12px 0', borderRadius: 10, border: '2px solid',
            borderColor: s.type === t ? '#7c3aed' : 'var(--border)',
            background: s.type === t ? '#ede9fe' : '#fff',
            color: s.type === t ? '#6d28d9' : 'var(--text)', fontWeight: 600, cursor: 'pointer',
          }}>
            {{ broadcast: '📣 Broadcast', drip: '💧 Drip Sequence' }[t]}
          </button>
        ))}
      </div>
      {s.type === 'drip' && (
        <div style={{ marginTop: 12, display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label className="form-label">Parent Campaign ID (leave empty for first step)</label>
            <input type="number" className="form-input" value={s.sourceId ?? ''} onChange={e => set('sourceId', e.target.value ? +e.target.value : null)} />
          </div>
          <div style={{ width: 120 }}>
            <label className="form-label">Delay (days)</label>
            <input type="number" className="form-input" min={0} max={365} value={s.dripDelayDays} onChange={e => set('dripDelayDays', +e.target.value)} />
          </div>
        </div>
      )}
    </div>
  </div>
);

const Step2: React.FC<{
  s: BuilderState;
  set: (k: keyof BuilderState, v: any) => void;
  segments: CampaignSegment[];
  reloadSegments: () => void;
}> = ({ s, set, segments, reloadSegments }) => {
  const [saving, setSaving]   = useState(false);
  const [segName, setSegName] = useState('');

  const saveSegment = async () => {
    if (!segName.trim() || !s.segmentFilters.rules.length) return;
    setSaving(true);
    try {
      const seg = await createSegment({
        name: segName, filters: s.segmentFilters, entity_type: s.segmentEntityType,
      });
      set('segmentId', seg.id);
      setSegName('');
      reloadSegments();
    } catch { alert('Failed to save segment'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <label className="form-label">Target Entity</label>
        <select className="filter-select" value={s.segmentEntityType} onChange={e => set('segmentEntityType', e.target.value)}>
          <option value="lead">Leads</option>
          <option value="contact">Contacts</option>
          <option value="customer">Customers</option>
        </select>
      </div>
      <div>
        <label className="form-label">Saved Segment</label>
        <select className="filter-select" value={s.segmentId ?? ''} onChange={e => set('segmentId', e.target.value ? +e.target.value : null)}>
          <option value="">— Build custom filters below —</option>
          {segments.filter(sg => sg.entity_type === s.segmentEntityType).map(sg => (
            <option key={sg.id} value={sg.id}>{sg.name} ({sg.estimated_reach.toLocaleString('en-IN')} {sg.entity_type}s)</option>
          ))}
        </select>
      </div>
      <div>
        <label className="form-label">Custom Filters</label>
        <FilterBuilder
          value={s.segmentFilters}
          onChange={f => set('segmentFilters', f)}
          entityType={s.segmentEntityType}
          channel={s.channel}
        />
      </div>
      {s.segmentFilters.rules.length > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label className="form-label">Save as segment (optional)</label>
            <input className="form-input" placeholder="Segment name…" value={segName} onChange={e => setSegName(e.target.value)} />
          </div>
          <button className="btn btn-secondary" onClick={saveSegment} disabled={saving || !segName.trim()}>
            {saving ? 'Saving…' : 'Save Segment'}
          </button>
        </div>
      )}
    </div>
  );
};

const Step3: React.FC<{
  s: BuilderState;
  set: (k: keyof BuilderState, v: any) => void;
  waTemplates: WaTemplate[];
}> = ({ s, set, waTemplates }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
    {s.channel === 'whatsapp' && (
      <div>
        <label className="form-label">WhatsApp Template *</label>
        <select className="filter-select" value={s.templateId ?? ''} onChange={e => set('templateId', e.target.value ? +e.target.value : null)}>
          <option value="">— Select approved template —</option>
          {waTemplates.filter(t => t.status === 'approved').map(t => (
            <option key={t.id} value={t.id}>{t.name} ({t.language})</option>
          ))}
        </select>
        {waTemplates.filter(t => t.status === 'approved').length === 0 && (
          <p style={{ fontSize: 12, color: '#dc2626', marginTop: 6 }}>
            No approved WA templates found. Add them under Settings → WA Templates.
          </p>
        )}
      </div>
    )}
    {(s.channel === 'sms' || s.channel === 'email') && (
      <div>
        <label className="form-label">Message Body *</label>
        <textarea className="form-input" rows={5} placeholder={
          s.channel === 'sms'
            ? 'Enter DLT-registered message (use {#var#} for variables)…'
            : 'Enter email body (HTML supported)…'
        } value={s.messageBody} onChange={e => set('messageBody', e.target.value)} style={{ resize: 'vertical' }} />
        {s.channel === 'sms' && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            {s.messageBody.length} chars · {Math.ceil(s.messageBody.length / 160)} SMS unit(s)
          </div>
        )}
      </div>
    )}
    {s.channel === 'whatsapp' && s.templateId && waTemplates.find(t => t.id === s.templateId)?.body_preview && (
      <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '12px 16px' }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#166534', marginBottom: 6 }}>PREVIEW</div>
        <div style={{ fontSize: 14, color: 'var(--text)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
          {waTemplates.find(t => t.id === s.templateId)!.body_preview}
        </div>
      </div>
    )}
  </div>
);

const Step4: React.FC<{ s: BuilderState; set: (k: keyof BuilderState, v: any) => void }> = ({ s, set }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
    <div>
      <label className="form-label">When to send</label>
      <div style={{ display: 'flex', gap: 10 }}>
        {[{ k: true, label: 'Send Now' }, { k: false, label: 'Schedule for Later' }].map(opt => (
          <button key={String(opt.k)} onClick={() => set('sendNow', opt.k)} style={{
            flex: 1, padding: '12px 0', borderRadius: 10, border: '2px solid',
            borderColor: s.sendNow === opt.k ? '#7c3aed' : 'var(--border)',
            background: s.sendNow === opt.k ? '#ede9fe' : '#fff',
            color: s.sendNow === opt.k ? '#6d28d9' : 'var(--text)', fontWeight: 600, cursor: 'pointer',
          }}>{opt.label}</button>
        ))}
      </div>
    </div>
    {!s.sendNow && (
      <div>
        <label className="form-label">Scheduled Date & Time</label>
        <input type="datetime-local" className="form-input" value={s.scheduledAt} onChange={e => set('scheduledAt', e.target.value)} />
      </div>
    )}
    <div>
      <label className="form-label">Throttle (messages per minute)</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <input type="range" min={1} max={200} value={s.throttlePerMinute}
          onChange={e => set('throttlePerMinute', +e.target.value)} style={{ flex: 1 }} />
        <span style={{ fontWeight: 700, width: 40, textAlign: 'right' }}>{s.throttlePerMinute}</span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
        At {s.throttlePerMinute} msg/min, sending to 500 contacts takes ~{Math.ceil(500 / s.throttlePerMinute)} min
      </div>
    </div>
  </div>
);

const Step5: React.FC<{ s: BuilderState; segments: CampaignSegment[]; waTemplates: WaTemplate[] }> = ({ s, segments, waTemplates }) => {
  const seg      = segments.find(x => x.id === s.segmentId);
  const template = waTemplates.find(x => x.id === s.templateId);

  const row = (label: string, value: string) => (
    <div style={{ display: 'flex', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ width: 160, flexShrink: 0, color: 'var(--text-muted)', fontSize: 13 }}>{label}</span>
      <span style={{ fontWeight: 600, fontSize: 14 }}>{value}</span>
    </div>
  );

  return (
    <div>
      {row('Campaign Name', s.name)}
      {row('Channel', s.channel.toUpperCase())}
      {row('Type', s.type)}
      {row('Segment', seg ? `${seg.name} (${seg.estimated_reach.toLocaleString('en-IN')} ${seg.entity_type}s)` : `Custom filters (${s.segmentEntityType}s)`)}
      {row('Template', template ? template.name : s.messageBody ? 'Custom message' : '—')}
      {row('Send', s.sendNow ? 'Immediately on launch' : s.scheduledAt)}
      {row('Throttle', `${s.throttlePerMinute} msg/min`)}
      {s.type === 'drip' && s.sourceId && row('Parent Campaign', `ID #${s.sourceId} (delay: ${s.dripDelayDays}d)`)}
    </div>
  );
};

// ── Main Builder ─────────────────────────────────────────────────────────────

const STEP_LABELS = ['Basic Info', 'Audience', 'Message', 'Schedule', 'Review'];

const CampaignBuilderPage: React.FC = () => {
  const history   = useHistory();
  const { id }    = useParams<{ id?: string }>();
  const isEdit    = !!id;

  const [state, setState]         = useState<BuilderState>(empty);
  const [segments, setSegments]   = useState<CampaignSegment[]>([]);
  const [templates, setTemplates] = useState<WaTemplate[]>([]);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  const set = (k: keyof BuilderState, v: any) => setState(s => ({ ...s, [k]: v }));

  const loadSegments = async () => {
    try { setSegments(await fetchSegments()); } catch {}
  };

  useEffect(() => {
    loadSegments();
    fetchWaTemplates().then(setTemplates).catch(() => {});
    if (isEdit && id) {
      fetchCampaign(+id).then(c => setState({
        ...empty,
        name: c.name, description: c.description ?? '', channel: c.channel, type: c.type,
        segmentId: c.segment_id ?? null, templateId: c.template_id ?? null,
        messageBody: c.message_body ?? '', sourceId: c.source_campaign_id ?? null,
        dripDelayDays: c.drip_delay_days ?? 0, throttlePerMinute: c.throttle_per_minute ?? 60,
        sendNow: !c.scheduled_at, scheduledAt: c.scheduled_at ?? '',
      })).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (status: 'draft' | 'scheduled' = 'draft') => {
    setSaving(true); setError('');
    try {
      let segmentId = state.segmentId;

      // Auto-create segment from custom filters if no saved segment is selected
      if (!segmentId) {
        if (!state.segmentFilters.rules.length) {
          setError('Please define an audience in step 2 (pick a saved segment or add at least one filter).');
          return;
        }
        const seg = await createSegment({
          name: `${state.name} — Audience`,
          filters: state.segmentFilters,
          entity_type: state.segmentEntityType,
        });
        segmentId = seg.id;
        setState(s => ({ ...s, segmentId: seg.id }));
      }

      const payload: any = {
        name: state.name, description: state.description, channel: state.channel, type: state.type,
        segment_id: segmentId, template_id: state.templateId, message_body: state.messageBody || null,
        source_campaign_id: state.sourceId, drip_delay_days: state.dripDelayDays,
        throttle_per_minute: state.throttlePerMinute,
        scheduled_at: !state.sendNow ? state.scheduledAt : null,
        status,
      };

      if (isEdit && id) {
        await updateCampaign(+id, payload);
      } else {
        await createCampaign(payload);
      }
      history.push('/campaigns');
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Save failed');
    } finally { setSaving(false); }
  };

  const step  = state.step;
  const steps = [1, 2, 3, 4, 5] as const;

  return (
    <div className="list-page" style={{ maxWidth: 720, margin: '0 auto' }}>
      <div className="page-hd">
        <div className="page-hd-left">
          <h1 className="page-title">{isEdit ? 'Edit Campaign' : 'New Campaign'}</h1>
        </div>
        <button className="btn btn-ghost" onClick={() => history.push('/campaigns')}>Cancel</button>
      </div>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 28, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)' }}>
        {steps.map(n => (
          <div key={n} onClick={() => n < step && setState(s => ({ ...s, step: n }))} style={{
            flex: 1, textAlign: 'center', padding: '10px 4px', fontSize: 12, fontWeight: 600, cursor: n < step ? 'pointer' : 'default',
            background: n === step ? '#7c3aed' : n < step ? '#ede9fe' : '#f8fafc',
            color: n === step ? '#fff' : n < step ? '#6d28d9' : '#94a3b8',
            borderRight: n < 5 ? '1px solid var(--border)' : 'none',
          }}>
            <div style={{ fontSize: 16, marginBottom: 2 }}>{n < step ? '✓' : n}</div>
            {STEP_LABELS[n - 1]}
          </div>
        ))}
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="data-card" style={{ padding: 24, marginBottom: 20 }}>
        {step === 1 && <Step1 s={state} set={set} />}
        {step === 2 && <Step2 s={state} set={set} segments={segments} reloadSegments={loadSegments} />}
        {step === 3 && <Step3 s={state} set={set} waTemplates={templates} />}
        {step === 4 && <Step4 s={state} set={set} />}
        {step === 5 && <Step5 s={state} segments={segments} waTemplates={templates} />}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <button className="btn btn-ghost" disabled={step === 1} onClick={() => setState(s => ({ ...s, step: (s.step - 1) as any }))}>
          ← Back
        </button>
        <div style={{ display: 'flex', gap: 10 }}>
          {step === 5 ? (
            <>
              <button className="btn btn-secondary" disabled={saving} onClick={() => handleSave('draft')}>
                {saving ? 'Saving…' : 'Save Draft'}
              </button>
              <button className="btn btn-primary" disabled={saving} onClick={() => handleSave('draft')}>
                {saving ? 'Saving…' : isEdit ? 'Update Campaign' : 'Create Campaign'}
              </button>
            </>
          ) : (
            <button className="btn btn-primary" disabled={!canProceed(state)} onClick={() => setState(s => ({ ...s, step: (s.step + 1) as any }))}>
              Next →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignBuilderPage;
